import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { useDispatch } from 'react-redux';
import { DistanceUnit } from '@grandvisionhq/utils';
import { setConfig } from '../actions.js';
import { getDateFnsLocaleModule } from '../date-fns-locales.js';
import { handleFormatDate, handleLongDate, handleMidsizeDate, handleShortDate, handleFormatTime } from '../date-time-helpers.js';
import { getLabel, getFormattedLabel, hasLabel } from '../helpers.js';
import IntlContext from '../IntlContext/IntlContext.js';
import { Regions, Countries, Locales, Currencies } from '../../shared/src/intl.js';

const supportedLocales = Object.keys(Locales);
const supportedCurrencies = Object.keys(Currencies);
const supportedDistanceUnits = Object.keys(DistanceUnit);
const settingsDefaults = {
    distanceUnit: DistanceUnit.km,
    dateFormat: {
        long: 'EEEE do MMMM, yyyy',
        midsize: 'dd MMMM yyyy',
        short: 'dd/MM/yyyy'
    },
    timeFormat: {
        default: 'H:mm'
    },
    region: Regions.EU
};
const mergeSettings = (settings)=>swcHelpers.objectSpread({}, settingsDefaults, settings)
;
const IntlProvider = ({ settings , labels , children  })=>{
    const { country , currency , locale , distanceUnit , dateFormat , timeFormat , region  } = mergeSettings(settings);
    const dispatch = useDispatch();
    const [dateFnsLocale, setDateFnsLocale] = React.useState();
    const value = React.useMemo(()=>({
            country,
            currency,
            distanceUnit,
            locale,
            dateFormat,
            timeFormat,
            dateFnsLocale,
            dateTime: makeDateTimeObject(dateFnsLocale, dateFormat, timeFormat),
            getLabel: (key, options)=>getLabel(labels, key, options)
            ,
            getFormattedLabel: (key, options)=>getFormattedLabel(labels, key, options)
            ,
            hasLabel: (key)=>hasLabel(labels, key)
            ,
            region
        })
    , [
        country,
        currency,
        dateFnsLocale,
        dateFormat,
        distanceUnit,
        labels,
        locale,
        timeFormat,
        region
    ]);
    React.useEffect(()=>{
        // Log some warnings if invalid settings were passed
        validateSettings({
            country,
            currency,
            locale,
            distanceUnit
        });
        // Init/update intl settings to our redux state
        dispatch(setConfig({
            country,
            currency,
            locale,
            distanceUnit,
            dateFormat,
            timeFormat,
            region
        }));
        // Lazy load the date-fns locale module for the locale that was passed
        const dateFnsLocaleModule = getDateFnsLocaleModule(locale);
        if (dateFnsLocaleModule) {
            dateFnsLocaleModule.then((module)=>{
                if (module && 'default' in module) {
                    setDateFnsLocale(module.default);
                }
            });
        } else {
            console.warn(`Could not find a date-fns locale obj for locale: ${locale}`);
        }
    }, [
        country,
        currency,
        dateFormat,
        dispatch,
        distanceUnit,
        locale,
        timeFormat,
        region
    ]);
    return /*#__PURE__*/ React.createElement(IntlContext.Provider, {
        value: value
    }, children);
};
function makeDateTimeObject(locale, dateFormat, timeFormat) {
    const options = !!locale ? {
        locale
    } : undefined;
    return {
        formatDate: (dateTime, key)=>{
            return handleFormatDate(dateFormat, dateTime, key, options);
        },
        formatLongDate: (dateTime)=>{
            return handleLongDate(dateFormat, dateTime, options);
        },
        formatMidsizeDate: (dateTime)=>{
            return handleMidsizeDate(dateFormat, dateTime, options);
        },
        formatShortDate: (dateTime)=>{
            return handleShortDate(dateFormat, dateTime, options);
        },
        formatTime: (dateTime, key)=>{
            return handleFormatTime(timeFormat, dateTime, key, options);
        }
    };
}
function validateSettings({ country , currency , locale , distanceUnit  }) {
    if (!(country in Countries)) {
        console.warn(`Invalid country "${country}" set on IntlProvider. Use 2 uppercase characters like "GB"`);
    }
    if (!(locale in Locales)) {
        console.warn(`Unsupported locale "${locale}" set on IntlProvider. Supported locales are: ${supportedLocales.join(', ')}`);
    }
    if (!supportedCurrencies.includes(currency)) {
        console.warn(`Unsupported or invalid currency "${currency}" set on IntlProvider. Supported currencies are: ${supportedCurrencies.join(', ')}`);
    }
    if (!distanceUnit || !supportedDistanceUnits.includes(distanceUnit)) {
        console.warn(`Unsupported or invalid distance unit "${distanceUnit}" set on IntlProvider. Supported distance units are: ${supportedDistanceUnits.join(', ')}`);
    }
}
var IntlProvider$1 = IntlProvider;

export { IntlProvider, IntlProvider$1 as default, settingsDefaults };
//# sourceMappingURL=IntlProvider.js.map
